import React, { useEffect, useState } from 'react';
import { KeyRound, Loader2, CheckCircle, XCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { fetchEligibility, fetchMyRequests, createRequest, updateRequest, requestChange } from '../../features/indicator/api/indicator';
import type { IndicatorEligibility, IndicatorRequest } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { INDICATOR } from '../../constants/testIds';

const inputClass =
  'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

const SOURCE_LABEL: Record<string, string> = {
  vip: 'VIP Aktif',
  course_access: 'Akses Course',
  manual_whitelist: 'Whitelist Admin',
};

export default function MemberIndicatorPage() {
  const [elig, setElig] = useState<IndicatorEligibility | null>(null);
  const [requests, setRequests] = useState<IndicatorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([fetchEligibility(), fetchMyRequests()])
      .then(([e, r]) => { setElig(e); setRequests(r); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const activeReq = requests.find((r) => r.status === 'pending' || r.status === 'processing');
  const revisionReq = requests.find((r) => r.status === 'needs_revision');
  const approvedReq = requests.find((r) => r.status === 'approved');
  const [changing, setChanging] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;
    setSubmitting(true);
    try {
      if (changing) {
        await requestChange(username.trim());
        toast.success('Permintaan ganti ID TradingView dikirim');
        setChanging(false);
      } else if (revisionReq) {
        await updateRequest(revisionReq.id, username.trim());
        toast.success('Permintaan diperbarui & dikirim ulang');
      } else {
        await createRequest(username.trim());
        toast.success('Permintaan akses indikator dikirim');
      }
      setUsername('');
      load();
    } catch (err: any) {
      toast.error('Gagal mengirim', { description: err.response?.data?.detail || err.message });
    } finally { setSubmitting(false); }
  };

  const canSubmit = elig?.eligible && !activeReq && !approvedReq;

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Akses Indikator">
      <div className="max-w-4xl" data-testid={INDICATOR.memberPage}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">TradingView</p>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Akses Indikator</h2>
          </div>
        </div>
        <p className="text-white/50 text-sm mb-6">Ajukan aktivasi username TradingView Anda untuk menerima akses indikator Cuantrader.</p>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} /></div>
        ) : (
          <div className="space-y-6">
            {/* Eligibility */}
            <div
              data-testid={INDICATOR.eligibilityCard}
              className={`rounded-2xl p-6 border flex items-start gap-4 ${elig?.eligible ? 'bg-emerald-400/5 border-emerald-400/20' : 'bg-red-400/5 border-red-400/20'}`}
            >
              {elig?.eligible ? <CheckCircle className="w-6 h-6 text-emerald-400 shrink-0" strokeWidth={1.5} /> : <XCircle className="w-6 h-6 text-red-400 shrink-0" strokeWidth={1.5} />}
              <div>
                <p className="text-white font-medium">{elig?.eligible ? 'Anda memenuhi syarat' : 'Belum memenuhi syarat'}</p>
                <p className="text-sm text-white/60 mt-1">{elig?.reason}</p>
                {elig?.eligible && elig.source && <p className="text-xs text-cyan-400 mt-2">Sumber: {SOURCE_LABEL[elig.source] || elig.source}</p>}
              </div>
            </div>

            {/* Approved: show active ID + change button */}
            {approvedReq && !activeReq && !changing && (
              <div data-testid={INDICATOR.approvedCard} className="bg-emerald-400/5 border border-emerald-400/20 rounded-2xl p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className={labelClass}>ID TradingView Aktif</p>
                    <p className="text-white font-medium text-lg">{approvedReq.tradingview_username}</p>
                    <p className="text-xs text-emerald-400 mt-1">Akses indikator Anda aktif.</p>
                  </div>
                  <button
                    type="button"
                    data-testid={INDICATOR.changeIdBtn}
                    onClick={() => { setChanging(true); setUsername(''); }}
                    className="border border-cyan-400/40 text-cyan-300 font-medium px-5 py-2 rounded-lg transition-colors hover:bg-cyan-400/10 flex items-center gap-2 shrink-0"
                  >
                    <KeyRound className="w-4 h-4" strokeWidth={2} /> Ganti ID TradingView
                  </button>
                </div>
              </div>
            )}

            {/* Form */}
            {canSubmit || revisionReq || changing ? (
              <form onSubmit={submit} data-testid={INDICATOR.form} className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                {changing && (
                  <div className="mb-4 text-sm text-cyan-300 bg-cyan-400/10 border border-cyan-400/20 rounded-lg px-4 py-2.5">
                    Ganti ID TradingView dari <span className="font-medium">{approvedReq?.tradingview_username}</span> ke ID baru. Permintaan akan ditinjau admin.
                  </div>
                )}
                {revisionReq && (
                  <div className="mb-4 text-sm text-amber-300 bg-amber-400/10 border border-amber-400/20 rounded-lg px-4 py-2.5">
                    Admin meminta revisi: {revisionReq.admin_note || 'perbarui username Anda'}
                  </div>
                )}
                <label className={labelClass}>{changing ? 'Username TradingView Baru' : 'Username TradingView'}</label>
                <div className="flex gap-2">
                  <input className={inputClass} data-testid={INDICATOR.usernameInput} value={username} onChange={(e) => setUsername(e.target.value)} placeholder="mis. trader_joe" />
                  <button type="submit" data-testid={INDICATOR.submit} disabled={submitting || !username.trim()} className="bg-cyan-400 text-[#050914] font-medium px-5 py-2 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2 shrink-0">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Send className="w-4 h-4" strokeWidth={2} />}
                    {changing ? 'Kirim Permintaan Ganti' : revisionReq ? 'Kirim Ulang' : 'Ajukan'}
                  </button>
                  {changing && (
                    <button type="button" data-testid={INDICATOR.cancelChangeBtn} onClick={() => { setChanging(false); setUsername(''); }} className="border border-white/15 text-white/60 font-medium px-4 py-2 rounded-lg transition-colors hover:bg-white/5 shrink-0">
                      Batal
                    </button>
                  )}
                </div>
              </form>
            ) : activeReq ? (
              <div className="bg-cyan-400/5 border border-cyan-400/20 rounded-2xl p-6 text-sm text-white/70">
                {activeReq.request_type === 'change' && activeReq.previous_tradingview_username
                  ? <>Permintaan ganti ID dari <span className="text-white font-medium">{activeReq.previous_tradingview_username}</span> ke <span className="text-white font-medium">{activeReq.tradingview_username}</span> sedang diproses. Menunggu keputusan admin.</>
                  : <>Permintaan Anda ({activeReq.tradingview_username}) sedang diproses. Menunggu keputusan admin.</>}
              </div>
            ) : null}

            {/* History */}
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/70 mb-3">Riwayat Permintaan</h3>
              {requests.length === 0 ? (
                <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada permintaan.</p>
              ) : (
                <div className="space-y-2">
                  {requests.map((r) => (
                    <div key={r.id} data-testid={INDICATOR.requestRow} className="flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium">{r.tradingview_username}</p>
                        <p className="text-xs text-white/40">Sumber: {SOURCE_LABEL[r.eligibility_source] || r.eligibility_source || '—'}{r.admin_note ? ` · Admin: ${r.admin_note}` : ''}</p>
                      </div>
                      <StatusBadge status={r.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
